// Helpers for formatting an excerpt dropped onto the Draft Canvas.
// Pure (no DOM, no React) so they can be unit-tested headlessly.

const QUOTE_PAIRS: Array<[string, string]> = [
  ['"', '"'],
  ["'", "'"],
  ["\u201C", "\u201D"], // “ ”
  ["\u2018", "\u2019"], // ‘ ’
  ["\u00AB", "\u00BB"], // « »
  ["\u201E", "\u201C"], // „ “
  ["`", "`"],
];

const escapeHtml = (s: string) =>
  s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

/** Strip ONE balanced wrapping pair of quote marks from a string. */
export const stripWrappingQuotes = (input: string): string => {
  let s = input.trim();
  // Strip up to two layers (handles nested " ' ... ' " or duplicate curly).
  for (let i = 0; i < 2; i += 1) {
    const first = s.charAt(0);
    const last = s.charAt(s.length - 1);
    const match = QUOTE_PAIRS.find(([open, close]) => first === open && last === close && s.length >= 2);
    if (!match) break;
    s = s.slice(1, -1).trim();
  }
  return s;
};

/** Strip wrapping quotes from raw HTML by walking only the leading/trailing text runs. */
const stripWrappingQuotesInHtml = (html: string): string => {
  let s = html.trim();
  // Strip leading text-quote (skip leading tags).
  const leadMatch = s.match(/^((?:<[^>]+>\s*)*)(["'\u201C\u2018\u00AB\u201E`])/);
  const tailMatch = s.match(/(["'\u201D\u2019\u00BB\u201C`])((?:\s*<\/[^>]+>)*)\s*$/);
  if (leadMatch && tailMatch) {
    const open = leadMatch[2];
    const close = tailMatch[1];
    const isPair = QUOTE_PAIRS.some(([o, c]) => o === open && c === close);
    if (isPair) {
      s = s.slice(0, leadMatch.index! + leadMatch[1].length)
        + s.slice(leadMatch.index! + leadMatch[0].length, tailMatch.index!)
        + s.slice(tailMatch.index! + 1);
    }
  }
  return s.trim();
};

const ALLOWED_INLINE_TAGS = new Set(["em", "i", "strong", "b", "u", "br", "span"]);

/**
 * Sanitize the body HTML of an excerpt for insertion inside a blockquote:
 * - drops block-level wrappers (<p>, <div>, <blockquote>, etc.)
 * - keeps inline emphasis (<em>, <i>, <strong>, <b>, <u>, <span>, <br>)
 * - drops dangerous tags (<script>, <style>, on* attrs)
 * - strips one balanced wrapping pair of quote marks
 */
export const sanitizeExcerptBodyHtml = (html: string): string => {
  if (!html) return "";
  // Remove dangerous nodes outright.
  let s = html.replace(/<(script|style)[\s\S]*?<\/\1>/gi, "");
  // Strip on*="..." event handler attributes.
  s = s.replace(/\son[a-z]+="[^"]*"/gi, "").replace(/\son[a-z]+='[^']*'/gi, "");
  // Replace block-level open/close tags with nothing (keep their inner text/markup).
  s = s.replace(/<\/?(p|div|blockquote|h[1-6]|ul|ol|li|pre|figure|figcaption|article|section|header|footer|aside)(\s[^>]*)?>/gi, "");
  // Drop any other tag not in the inline allowlist.
  s = s.replace(/<\/?([a-zA-Z][a-zA-Z0-9-]*)(\s[^>]*)?>/g, (match, tag: string) => {
    return ALLOWED_INLINE_TAGS.has(tag.toLowerCase()) ? match : "";
  });
  s = stripWrappingQuotesInHtml(s);
  return s.trim();
};

export type ExcerptCitationParts = {
  author?: string | null;
  publication?: string | null;
  year?: number | string | null;
  pageStart?: number | null;
  pageEnd?: number | null;
  // Source-type-aware extras (added 2026-05-23 for podcast citations).
  sourceType?: string | null;
  containerTitle?: string | null;
  accessedDate?: string | null;
  timestampSeconds?: number | null;
  // Amazon Associates affiliate link wrapper for the publication title
  // (added 2026-06-06). When provided, the publication `<em>` is wrapped in
  // a dotted-underline anchor that inherits the surrounding link color.
  affiliateHref?: string | null;
};

const formatPageRange = (pageStart: number | null | undefined, pageEnd: number | null | undefined) => {
  if (!pageStart) return null;
  if (pageEnd && pageEnd !== pageStart) return `pp. ${pageStart}\u2013${pageEnd}`;
  return `p. ${pageStart}`;
};

/** Format a positive number of seconds as M:SS or H:MM:SS. Returns null for null/0/negative. */
export const formatTimestamp = (seconds: number | null | undefined): string | null => {
  if (seconds == null || !Number.isFinite(seconds) || seconds <= 0) return null;
  const total = Math.floor(seconds);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const pad = (n: number) => n.toString().padStart(2, "0");
  return h > 0 ? `${h}:${pad(m)}:${pad(s)}` : `${m}:${pad(s)}`;
};

/** Parse the seconds value out of a `#t=<seconds>` media fragment on a URL. */
export const parseMediaFragmentSeconds = (url: string | null | undefined): number | null => {
  if (!url) return null;
  const m = url.match(/#t=(\d+(?:\.\d+)?)/);
  if (!m) return null;
  const n = Number.parseFloat(m[1]);
  return Number.isFinite(n) && n > 0 ? n : null;
};

/**
 * Build the muted attribution paragraph.
 * - Podcast (sourceType === "podcast") with container_title:
 *     "Author, *Show Name*, "Episode Title," YYYY-MM-DD, 12:34, accessed YYYY-MM-DD"
 * - Otherwise (existing behavior, unchanged):
 *     "Author, *Publication*, Year, pp. X–Y"
 * Returns "" when no metadata is present.
 */
export const buildExcerptCitationHtml = ({
  author,
  publication,
  year,
  pageStart,
  pageEnd,
  sourceType,
  containerTitle,
  accessedDate,
  timestampSeconds,
  affiliateHref,
}: ExcerptCitationParts): string => {
  const authorClean = author?.trim() || null;
  const publicationClean = publication?.trim() || null;
  const yearClean = year != null && String(year).trim() !== "" ? String(year).trim() : null;
  const pageRange = formatPageRange(pageStart ?? null, pageEnd ?? null);
  const containerClean = containerTitle?.trim() || null;
  const accessedClean = accessedDate?.trim() || null;
  const timestampStr = formatTimestamp(timestampSeconds ?? null);
  const hrefClean = affiliateHref?.trim() || null;

  // AFFILIATE_* constants are inlined here to keep this module DOM/React-free.
  const AFFILIATE_TOOLTIP = "Opens on Amazon — purchases support the author";
  const AFFILIATE_CLASS = "underline decoration-dotted underline-offset-2 hover:decoration-solid";
  const wrapAffiliate = (innerHtml: string): string =>
    hrefClean
      ? `<a href="${escapeHtml(hrefClean)}" target="_blank" rel="nofollow sponsored noopener noreferrer" title="${AFFILIATE_TOOLTIP}" aria-label="${AFFILIATE_TOOLTIP}" class="${AFFILIATE_CLASS}">${innerHtml}</a>`
      : innerHtml;

  // Note: year is intentionally omitted from the in-draft inline attribution.
  // The year still appears in Citations Preview / footnotes / endnotes /
  // bibliography via the CSL-JSON renderer (`renderCitation`). Keeping `year`
  // in `ExcerptCitationParts` for back-compat with callers; it is unused here.
  void yearClean;

  if (sourceType === "podcast" && containerClean) {
    const parts: string[] = [];
    if (authorClean) parts.push(escapeHtml(authorClean));
    parts.push(wrapAffiliate(`<em>${escapeHtml(containerClean)}</em>`));
    if (publicationClean) parts.push(`\u201C${escapeHtml(publicationClean)}\u201D`);
    if (timestampStr) parts.push(escapeHtml(timestampStr));
    if (accessedClean) parts.push(`accessed ${escapeHtml(accessedClean)}`);
    return `<p class="text-sm text-muted-foreground not-italic">\u2014 ${parts.join(", ")}</p>`;
  }

  const parts: string[] = [];
  if (authorClean) parts.push(escapeHtml(authorClean));
  if (publicationClean) parts.push(wrapAffiliate(`<em>${escapeHtml(publicationClean)}</em>`));
  if (pageRange) parts.push(escapeHtml(pageRange));

  if (parts.length === 0) return "";
  return `<p class="text-sm text-muted-foreground not-italic">\u2014 ${parts.join(", ")}</p>`;
};

export const escapeExcerptText = escapeHtml;
